// src/pages/etc/InsuranceAnnualSummary.jsx
// 개인별 연간 사회보험료 합계
//  - 탭: 건강+요양 / 국민연금 / 고용보험 / 산재보험
//  - 열: 이름 / 1~12월 / 합계
//  - 기준(basis): 총액 / 사업주부담 / 본인부담
//  - 이름 순서: 해당 연도 중 '순서가 저장된 가장 마지막(최신) 월' 기준
//    (예: 1월 1번 서복성, 11월 1번 윤길상 → 합계는 윤길상이 1번)
import React, { useEffect, useState } from "react";
import api from "../../api";

// 연월 드롭다운용
const NOW_Y = new Date().getFullYear();
const YEARS = [];
for (let y = NOW_Y + 1; y >= 2024; y--) YEARS.push(y);
const MM = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));

const TABS = [
  { key: "건강", label: "건강", types: ["건강"] },
  { key: "요양", label: "요양", types: ["요양"] },
  { key: "국민연금", label: "국민연금", types: ["국민연금"] },
  { key: "고용_실업", label: "고용(실업급여)", types: ["고용_실업"] },
  { key: "고용_고안", label: "고용(고안직능)", types: ["고용_고안"] },
  { key: "산재보험", label: "산재보험", types: ["산재"] },
];
const BASES = [
  { key: "total", label: "총액" },
  { key: "employer", label: "사업주부담" },
  { key: "worker", label: "본인부담" },
];

// 소속별 보기: 데이터에 있는 소속(근무지)을 그대로 한 행씩.
//  매핑 없는 사람은 "미지정" 으로 모음.
const NONE_KEY = "__none__";
const affGroupLabel = (key) => (key === NONE_KEY ? "미지정" : key);

const fmt = (n) => Number(n || 0).toLocaleString();
const normName = (s) => String(s ?? "").replace(/\s+/g, "").trim();
const fmtYm = (s) => (s && s.length >= 7 ? `${s.slice(0, 4)}년 ${+s.slice(5, 7)}월` : s);

// 한 slot({total, employer})에서 기준값 추출
const slotValue = (slot, basis) => {
  const t = slot?.total || 0;
  const e = slot?.employer || 0;
  if (basis === "employer") return e;
  if (basis === "worker") return t - e; // 본인부담 = 총액 - 사업주부담
  return t; // total
};

export default function InsuranceAnnualSummary() {
  const [year, setYear] = useState(String(NOW_Y));
  const [tabKey, setTabKey] = useState("건강");
  const [basis, setBasis] = useState("total");
  const [view, setView] = useState("person"); // person(개인별) | affiliation(소속별)
  const [affFilter, setAffFilter] = useState("전체"); // 소속 칩 필터

  const [data, setData] = useState(null); // annual 응답
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [query, setQuery] = useState("");

  const tab = TABS.find((t) => t.key === tabKey) || TABS[0];

  const load = async (y = year) => {
    if (!y) { setData(null); return; }
    setBusy(true); setMsg("");
    try {
      const r = await api.get("/insurance/annual/", { params: { year: y } });
      setData(r.data || null);
      if (!r.data?.people?.length) setMsg(`${y}년에 적재된 사회보험 명세가 없습니다.`);
    } catch (e) {
      setData(null);
      setMsg("불러오기 실패: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  useEffect(() => { load(year); /* eslint-disable-next-line */ }, [year]);

  // ── 이름 순서 ──
  const nameOrder = (data?.name_order || []).map(normName);
  const orderIndex = (name) => {
    // 인사이동자는 명단에서 '아래쪽(현재 소속)' 위치 채택 → lastIndexOf
    const i = nameOrder.lastIndexOf(normName(name));
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };


  // 데이터 있는 월(헤더 흐림 처리용)
  const presentMonths = new Set((data?.months || []).map((m) => m.slice(5, 7)));

  // ── 행 계산 ──
  // 사람별: 월별 셀값(현재 탭/기준) + 연 합계
  const cellOf = (person, mm) => {
    const bm = person.by_month?.[`${year}-${mm}`];
    if (!bm) return 0;
    return tab.types.reduce((s, t) => s + slotValue(bm[t], basis), 0);
  };

  const people = (data?.people || []).map((p) => {
    const months = {};
    let sum = 0;
    MM.forEach((mm) => { const v = cellOf(p, mm); months[mm] = v; sum += v; });
    return { name: p.name, birth: p.birth, affiliation: p.affiliation || "", months, sum };
  });

  // ── 소속 칩 (전체 + 데이터에 있는 소속들, 인원수 포함) ──
  const affChips = (() => {
    const cnt = {};
    people.forEach((p) => { const k = p.affiliation || "미지정"; cnt[k] = (cnt[k] || 0) + 1; });
    const keys = Object.keys(cnt).sort((a, b) => {
      if (a === "미지정") return 1;
      if (b === "미지정") return -1;
      return a.localeCompare(b, "ko");
    });
    return [{ key: "전체", cnt: people.length }, ...keys.map((k) => ({ key: k, cnt: cnt[k] }))];
  })();

  // 소속 칩으로 1차 필터
  const affPicked = affFilter === "전체"
    ? people
    : people.filter((p) => (p.affiliation || "미지정") === affFilter);

  // 검색 (소속 칩 결과에 적용)
  const q = query.trim();
  const qNum = q.replace(/[, ]/g, "");
  const filtered = !q ? affPicked : affPicked.filter((p) => {
    const hay = [p.name, p.birth, p.affiliation].join(" ");
    if (hay.includes(q)) return true;
    if (qNum) {
      const amts = [...MM.map((mm) => p.months[mm]), p.sum].map(String);
      return amts.some((a) => a.includes(qNum));
    }
    return false;
  });

  // 정렬: 마지막 달 순서 → 가나다
  const sorted = filtered.slice().sort((a, b) => {
    const d = orderIndex(a.name) - orderIndex(b.name);
    return d !== 0 ? d : String(a.name).localeCompare(String(b.name), "ko");
  });

  // 월별 합계 / 전체 합계
  const colTotal = (mm) => sorted.reduce((s, p) => s + p.months[mm], 0);
  const grandTotal = sorted.reduce((s, p) => s + p.sum, 0);

  // ── 소속별 그룹 행 (현재 탭/기준, filtered 기준) ──
  //  데이터에 있는 소속을 그대로 한 행씩. 미지정(소속 없음)은 맨 뒤.
  const affRows = (() => {
    const groups = {};
    filtered.forEach((p) => {
      const gk = p.affiliation || NONE_KEY;
      if (!groups[gk]) {
        groups[gk] = { key: gk, label: affGroupLabel(gk), months: Object.fromEntries(MM.map((m) => [m, 0])), sum: 0 };
      }
      const g = groups[gk];
      MM.forEach((mm) => { g.months[mm] += p.months[mm]; });
      g.sum += p.sum;
    });
    return Object.values(groups).sort((a, b) => {
      if (a.key === NONE_KEY) return 1;            // 미지정은 맨 뒤
      if (b.key === NONE_KEY) return -1;
      return a.label.localeCompare(b.label, "ko"); // 그 외 가나다순
    });
  })();

  const basisLabel = BASES.find((b) => b.key === basis)?.label || "총액";

  // ── 엑셀 다운로드 (보기 모드별: 개인별 = 사람×월, 소속별 = 소속×월. 각 보험종류 1시트) ──
  const downloadExcel = async () => {
    if (!data?.people?.length) { setMsg("내보낼 데이터가 없어요."); return; }
    let XS;
    try { const mod = await import("xlsx-js-style"); XS = mod.default || mod; }
    catch { setMsg("엑셀 모듈이 없습니다. 터미널에서 'npm install xlsx-js-style' 후 다시 시도하세요."); return; }

    const border = {
      top: { style: "thin", color: { rgb: "BBBBBB" } }, bottom: { style: "thin", color: { rgb: "BBBBBB" } },
      left: { style: "thin", color: { rgb: "BBBBBB" } }, right: { style: "thin", color: { rgb: "BBBBBB" } },
    };

    // 특정 보험종류(types)에 대한 사람별 월 데이터 (소속 칩 필터 반영)
    const personRows = (types) => (data.people || [])
      .filter((p) => affFilter === "전체" || (p.affiliation || "미지정") === affFilter)
      .map((p) => {
        const months = {}; let sum = 0;
        MM.forEach((mm) => {
          const bm = p.by_month?.[`${year}-${mm}`];
          const v = bm ? types.reduce((s, t) => s + slotValue(bm[t], basis), 0) : 0;
          months[mm] = v; sum += v;
        });
        return { name: p.name, birth: p.birth, affiliation: p.affiliation || "", months, sum };
      }).sort((a, b) => {
        const d = orderIndex(a.name) - orderIndex(b.name);
        return d !== 0 ? d : String(a.name).localeCompare(String(b.name), "ko");
      });

    // 특정 보험종류(types)에 대한 소속별 월 데이터 (실제 소속 그대로)
    const affRowsFor = (types) => {
      const groups = {};
      personRows(types).forEach((p) => {
        const gk = p.affiliation || NONE_KEY;
        if (!groups[gk]) groups[gk] = { key: gk, label: affGroupLabel(gk), months: Object.fromEntries(MM.map((m) => [m, 0])), sum: 0 };
        const g = groups[gk];
        MM.forEach((mm) => { g.months[mm] += p.months[mm]; });
        g.sum += p.sum;
      });
      return Object.values(groups).sort((a, b) => {
        if (a.key === NONE_KEY) return 1;
        if (b.key === NONE_KEY) return -1;
        return a.label.localeCompare(b.label, "ko");
      });
    };

    const wb = XS.utils.book_new();

    TABS.forEach((T) => {
      let headers, body, totalRow, firstNumCol, colWidths;

      if (view === "affiliation") {
        const rows = affRowsFor(T.types);
        headers = ["소속", ...MM.map((mm) => `${+mm}월`), "합계"];
        body = rows.map((g) => [g.label, ...MM.map((mm) => g.months[mm]), g.sum]);
        totalRow = ["합계", ...MM.map((mm) => rows.reduce((s, g) => s + g.months[mm], 0)), rows.reduce((s, g) => s + g.sum, 0)];
        firstNumCol = 1;
        colWidths = [{ wch: 14 }, ...MM.map(() => ({ wch: 11 })), { wch: 13 }];
      } else {
        const rows = personRows(T.types);
        headers = ["연번", "이름", "생년월일", "소속", ...MM.map((mm) => `${+mm}월`), "합계"];
        body = rows.map((p, i) => [i + 1, p.name, p.birth, p.affiliation || "미지정", ...MM.map((mm) => p.months[mm]), p.sum]);
        totalRow = ["", "", "", "합계", ...MM.map((mm) => rows.reduce((s, p) => s + p.months[mm], 0)), rows.reduce((s, p) => s + p.sum, 0)];
        firstNumCol = 4;
        colWidths = [{ wch: 5 }, { wch: 11 }, { wch: 11 }, { wch: 14 }, ...MM.map(() => ({ wch: 11 })), { wch: 13 }];
      }

      const aoa = [headers, ...body, totalRow];
      const ws = XS.utils.aoa_to_sheet(aoa);
      ws["!cols"] = colWidths;

      const range = XS.utils.decode_range(ws["!ref"]);
      for (let r = range.s.r; r <= range.e.r; r++) {
        for (let c = range.s.c; c <= range.e.c; c++) {
          const addr = XS.utils.encode_cell({ r, c });
          if (!ws[addr]) ws[addr] = { t: "s", v: "" };
          const isHeader = r === 0;
          const isTotal = r === range.e.r;
          const isNum = c >= firstNumCol;
          ws[addr].s = {
            border,
            alignment: { horizontal: isNum && !isHeader ? "right" : "center", vertical: "center" },
            font: { bold: isHeader || isTotal },
            fill: isHeader ? { fgColor: { rgb: "F1F5F9" } } : (isTotal ? { fgColor: { rgb: "F8FAFC" } } : { fgColor: { rgb: "FFFFFF" } }),
          };
          if (isNum && !isHeader && typeof ws[addr].v === "number") ws[addr].z = "#,##0";
        }
      }
      XS.utils.book_append_sheet(wb, ws, T.label.replace(/[\\/?*[\]:]/g, ""));
    });

    const viewLabel = view === "affiliation" ? "소속별" : "개인별";
    XS.writeFile(wb, `[ERP] ${year}년 ${viewLabel} 연간 사회보험료(${basisLabel}).xlsx`);
  };

  const S = {
    wrap: { padding: 16, fontSize: 13, color: "#1f2937" },
    bar: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12 },
    chip: (a) => ({
      padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontWeight: 600,
      border: a ? "1px solid #2563eb" : "1px solid #d1d5db", background: a ? "#2563eb" : "#fff", color: a ? "#fff" : "#374151",
    }),
    btnSm: (c, d) => ({
      width: "auto", padding: "4px 10px", background: d ? "#9ca3af" : c, color: "#fff",
      border: "none", borderRadius: 6, fontWeight: 600, cursor: d ? "not-allowed" : "pointer", fontSize: 12, whiteSpace: "nowrap",
    }),
    sel: { padding: "3px 6px", borderRadius: 4, border: "1px solid #cbd5e1" },
    input: { padding: "4px 8px", borderRadius: 6, border: "1px solid #cbd5e1" },
    th: { background: "#f1f5f9", padding: "6px 8px", borderBottom: "1px solid #cbd5e1", textAlign: "left", whiteSpace: "nowrap", position: "sticky", top: 0 },
    thR: { background: "#f1f5f9", padding: "6px 8px", borderBottom: "1px solid #cbd5e1", textAlign: "right", whiteSpace: "nowrap", position: "sticky", top: 0 },
    td: { padding: "5px 8px", borderBottom: "1px solid #eef2f7" },
    tdR: { padding: "5px 8px", borderBottom: "1px solid #eef2f7", textAlign: "right" },
  };

  const numCell = (v) => (
    <span style={{ color: v ? "#1f2937" : "#cbd5e1" }}>{v ? fmt(v) : "-"}</span>
  );

  return (
    <div style={S.wrap}>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>개인별 연간 사회보험료 합계</h2>
      <p style={{ color: "#6b7280", marginBottom: 14 }}>
        <b>사회보험 부문 구분</b>에서 적재한 월별 명세를 사람 기준으로 한 해 모아 보여줍니다.
        이름 순서는 <b>해당 연도 중 순서가 저장된 가장 마지막 월</b> 기준입니다.
      </p>

      <div style={S.bar}>
        <label style={{ whiteSpace: "nowrap" }}>연도</label>
        <select value={year} onChange={(e) => setYear(e.target.value)} style={{ ...S.sel, width: 100 }}>
          {YEARS.map((y) => <option key={y} value={y}>{y}년</option>)}
        </select>

        <div style={{ display: "flex", gap: 4, marginLeft: 8 }}>
          {TABS.map((t) => (
            <div key={t.key} style={S.chip(tabKey === t.key)} onClick={() => setTabKey(t.key)}>{t.label}</div>
          ))}
        </div>

        <span style={{ marginLeft: 12, whiteSpace: "nowrap" }}>기준:</span>
        <div style={{ display: "flex", gap: 4 }}>
          {BASES.map((b) => (
            <div key={b.key} style={S.chip(basis === b.key)} onClick={() => setBasis(b.key)}>{b.label}</div>
          ))}
        </div>

        <span style={{ marginLeft: 12, whiteSpace: "nowrap" }}>보기:</span>
        <div style={{ display: "flex", gap: 4 }}>
          <div style={S.chip(view === "person")} onClick={() => setView("person")}>개인별</div>
          <div style={S.chip(view === "affiliation")} onClick={() => setView("affiliation")}>소속별</div>
        </div>

        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="검색: 이름 / 생년월일 / 금액"
          style={{ ...S.input, width: 220, marginLeft: 8 }} />
        {q && <button style={S.btnSm("#64748b", false)} onClick={() => setQuery("")}>지우기</button>}

        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <button style={S.btnSm("#15803d", busy)} disabled={busy} onClick={downloadExcel}>📥 엑셀(전체)</button>
          <button style={S.btnSm("#0ea5e9", busy)} disabled={busy} onClick={() => load(year)}>새로고침</button>
        </div>
      </div>

      {data?.people?.length > 0 && (
        <div style={{ ...S.bar, marginBottom: 8 }}>
          <span style={{ color: "#475569", fontWeight: 600, whiteSpace: "nowrap" }}>소속:</span>
          {affChips.map((c) => (
            <div key={c.key} style={S.chip(affFilter === c.key)} onClick={() => setAffFilter(c.key)}>
              {c.key} {c.cnt}
            </div>
          ))}
        </div>
      )}

      <div style={{ marginBottom: 10, color: "#334155", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span>{tab.label} · {basisLabel} · {view === "person" ? `인원 ${filtered.length}명` : `소속 ${affRows.length}개`}{affFilter !== "전체" ? ` · ${affFilter}` : ""}</span>
        {data?.order_month
          ? <span style={{ color: "#2563eb" }}>· 이름 순서 기준: {fmtYm(data.order_month)}</span>
          : <span style={{ color: "#b91c1c" }}>· 저장된 이름 순서 없음(가나다순 표시)</span>}
        {data?.months?.length ? <span style={{ color: "#94a3b8" }}>· 데이터 월: {data.months.map((m) => +m.slice(5, 7) + "월").join(", ")}</span> : null}
      </div>

      {msg && (
        <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", padding: "6px 10px", borderRadius: 6, marginBottom: 12, color: "#075985" }}>{msg}</div>
      )}

      <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 8 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 980 }}>
          <thead>
            <tr>
              {view === "person" ? (
                <>
                  <th style={{ ...S.th, width: 44 }}>연번</th>
                  <th style={{ ...S.th, width: 110 }}>이름</th>
                  <th style={{ ...S.th, width: 160 }}>소속</th>
                </>
              ) : (
                <th style={{ ...S.th, width: 140 }}>소속</th>
              )}
              {MM.map((mm) => (
                <th key={mm} style={{ ...S.thR, color: presentMonths.has(mm) ? "#1f2937" : "#cbd5e1" }}>{+mm}월</th>
              ))}
              <th style={{ ...S.thR, fontWeight: 700 }}>합계</th>
            </tr>
          </thead>

          {view === "person" ? (
            <tbody>
              {sorted.length === 0 ? (
                <tr><td style={S.td} colSpan={16}>표시할 데이터가 없습니다.</td></tr>
              ) : (
                sorted.map((p, idx) => (
                  <tr key={`${p.name}|${p.birth}`}>
                    <td style={{ ...S.td, color: "#94a3b8" }}>{idx + 1}</td>
                    <td style={{ ...S.td, fontWeight: 600 }}>
                      {p.name}
                      {p.birth ? <span style={{ color: "#94a3b8", fontWeight: 400, marginLeft: 6, fontSize: 11 }}>{p.birth}</span> : null}
                    </td>
                    <td style={{ ...S.td, whiteSpace: "nowrap", color: p.affiliation ? "#1f2937" : "#cbd5e1" }}>{p.affiliation || "미지정"}</td>

                    {MM.map((mm) => <td key={mm} style={S.tdR}>{numCell(p.months[mm])}</td>)}
                    <td style={{ ...S.tdR, fontWeight: 700 }}>{fmt(p.sum)}</td>
                  </tr>
                ))
              )}
            </tbody>
          ) : (
            <tbody>
              {affRows.length === 0 ? (
                <tr><td style={S.td} colSpan={14}>표시할 데이터가 없습니다.</td></tr>
              ) : (
                affRows.map((g) => (
                  <tr key={g.key} style={g.key === NONE_KEY ? { background: "#fef2f2" } : {}}>
                    <td style={{ ...S.td, fontWeight: 600, whiteSpace: "nowrap", color: g.key === NONE_KEY ? "#b91c1c" : "#1f2937" }}>{g.label}</td>
                    {MM.map((mm) => <td key={mm} style={S.tdR}>{numCell(g.months[mm])}</td>)}
                    <td style={{ ...S.tdR, fontWeight: 700 }}>{fmt(g.sum)}</td>
                  </tr>
                ))
              )}
            </tbody>
          )}

          {((view === "person" && sorted.length > 0) || (view === "affiliation" && affRows.length > 0)) && (
            <tfoot>
              <tr style={{ background: "#f8fafc", fontWeight: 700 }}>
                <td style={S.td} colSpan={view === "person" ? 3 : 1}>
                  합계 ({view === "person" ? `${filtered.length}명` : `${affRows.length}개 소속`}{q ? " · 검색" : ""})
                </td>
                {MM.map((mm) => <td key={mm} style={S.tdR}>{fmt(colTotal(mm))}</td>)}
                <td style={S.tdR}>{fmt(grandTotal)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}